import { AssistantMessage, ChatMessage } from '../types'

/**
 * The one place an id-less assistant message is built. `BaseMessage.id` is
 * typed as required, so the placeholder state (`clientId` set, `id` absent —
 * what `adoptAssistantPlaceholder` matches on) only exists behind this cast.
 * Keeping the cast here, next to the adoption protocol it enables, is what
 * keeps it out of the stream handlers.
 */
export function createAssistantPlaceholder(
  conversationId: string,
  clientId: string,
  fields?: Pick<AssistantMessage, 'useAdvancedReasoning' | 'model_id'>,
): AssistantMessage {
  return {
    role: 'assistant',
    conversation_id: conversationId,
    clientId,
    content: '',
    given_feedback: null,
    ...fields,
  } as AssistantMessage
}

function contentLength(message: ChatMessage): number {
  return typeof message.content === 'string' ? message.content.length : 0
}

function mergeStreamCopies(existing: AssistantMessage, incoming: AssistantMessage): AssistantMessage {
  return contentLength(existing) > contentLength(incoming) ? { ...incoming, ...existing } : { ...existing, ...incoming }
}

/**
 * Index of a row that already carries `incoming.id`, other than `except`.
 * Two streams can fold the same turn: a `/subscribe` that attached (or
 * reconnected) after a queued turn started keeps writing until the sender's
 * own POST stream reaches `metadata` and supersedes it. Whatever it appended
 * for this message sits *above* the user bubble, since the queued commit
 * hadn't run yet.
 */
function twinIndexOf(messages: Array<ChatMessage>, incoming: AssistantMessage, except: number): number {
  if (incoming.id === undefined) return -1
  return messages.findIndex((message, index) => index !== except && message.id === incoming.id)
}

/**
 * Adopt in place: position and clientId survive so the group key never
 * changes. A twin row written by a superseded stream folds into the adoption
 * and is dropped — the placeholder holds the message's true position, so the
 * reply never renders both above and below the message that produced it
 * (HIG-6215). Whichever copy has streamed further keeps its content.
 */
function adoptAt(messages: Array<ChatMessage>, index: number, incoming: AssistantMessage): Array<ChatMessage> {
  const twinIndex = twinIndexOf(messages, incoming, index)
  const twin = twinIndex === -1 ? undefined : (messages[twinIndex] as AssistantMessage)
  const merged = twin === undefined ? incoming : mergeStreamCopies(twin, incoming)
  const adopted = { ...messages[index], ...merged, clientId: messages[index].clientId } as ChatMessage
  return messages.flatMap((message, i) => (i === index ? [adopted] : i === twinIndex ? [] : [message]))
}

/** No placeholder to adopt: merge into the row that already carries the id, else append. */
function appendOrMerge(messages: Array<ChatMessage>, incoming: AssistantMessage): Array<ChatMessage> {
  const twinIndex = twinIndexOf(messages, incoming, -1)
  if (twinIndex === -1) {
    return [...messages, incoming]
  }
  const updated = [...messages]
  updated[twinIndex] = mergeStreamCopies(updated[twinIndex] as AssistantMessage, incoming)
  return updated
}

/**
 * Replace the optimistic assistant placeholder (matched by clientId, no
 * backend id yet) with the first real assistant message of the turn. The
 * placeholder's position and clientId are preserved so the group key never
 * changes across the placeholder→confirmed seam. Appends the incoming message
 * when no unadopted placeholder exists (e.g. it was removed by an error path).
 */
export function adoptAssistantPlaceholder(
  messages: Array<ChatMessage>,
  clientId: string,
  incoming: AssistantMessage,
): Array<ChatMessage> {
  const index = messages.findIndex((message) => message.clientId === clientId && message.id === undefined)
  if (index === -1) {
    return appendOrMerge(messages, incoming)
  }
  return adoptAt(messages, index, incoming)
}

/**
 * Resume-path adoption. A resubscribe (drawer reopen mid-stream) doesn't know
 * which submit minted the turn's placeholder, so a new assistant message
 * adopts the chat's first unadopted one — there is at most one per chat,
 * since a turn commits exactly one and it is adopted or removed before the
 * next commits. Appends when none exists. Without this, a resume that races
 * the original submit strands an id-less placeholder bubble in the transcript
 * and appends the real assistant message beside it.
 */
export function adoptAnyAssistantPlaceholder(
  messages: Array<ChatMessage>,
  incoming: AssistantMessage,
): Array<ChatMessage> {
  const index = messages.findIndex(
    (message) => message.role === 'assistant' && message.clientId !== undefined && message.id === undefined,
  )
  if (index === -1) {
    return appendOrMerge(messages, incoming)
  }
  return adoptAt(messages, index, incoming)
}

/**
 * Drop a placeholder that was never adopted — a committed turn that produced
 * no assistant events would otherwise leave an id-less message behind forever.
 * Returns the input array unchanged (same reference) when nothing matches.
 */
export function removeUnadoptedPlaceholder(messages: Array<ChatMessage>, clientId: string): Array<ChatMessage> {
  const index = messages.findIndex((message) => message.clientId === clientId && message.id === undefined)
  if (index === -1) {
    return messages
  }
  return [...messages.slice(0, index), ...messages.slice(index + 1)]
}

/**
 * Release an abandoned assistant row (a fallback restart's unfinished step).
 * A row that adopted a placeholder reverts to it, so the next attempt adopts
 * the same clientId and the group key never changes; any other row is dropped.
 * Returns the input array unchanged (same reference) when the id isn't found.
 */
export function releaseAssistantRow(messages: Array<ChatMessage>, messageId: string): Array<ChatMessage> {
  const index = messages.findIndex((message) => message.role === 'assistant' && message.id === messageId)
  if (index === -1) {
    return messages
  }
  const row = messages[index] as AssistantMessage
  if (row.clientId === undefined) {
    return [...messages.slice(0, index), ...messages.slice(index + 1)]
  }
  const placeholder = createAssistantPlaceholder(row.conversation_id, row.clientId, {
    useAdvancedReasoning: row.useAdvancedReasoning,
    model_id: row.model_id,
  })
  return [...messages.slice(0, index), placeholder, ...messages.slice(index + 1)]
}
